/**
 * Shared uniqueness helpers for Custom List alternate import values (HA-2496).
 *
 * MUST behave byte-identically with the TS counterpart at
 * `athena/amplify/custom-lists/lib/alternateImportValues.ts`.
 *
 * Normalization rule:
 *   trim() then toLowerCase(). null / undefined / whitespace-only -> ''
 *
 * Uniqueness rule (per list):
 *   One flat pool of names per list. Every option contributes its label AND
 *   each of its alternate import values, and no normalized value may appear
 *   twice. For an option with label `A` and alternates `B`, `C`: `A`/`B`/`C`
 *   must differ from each other, and no other option may use `A`, `B` or `C`
 *   as either a label or an alternate.
 *
 *   DISABLED OPTIONS STILL HOLD THEIR NAMES. A disabled option is excluded
 *   from MATCHING (`shiftTypeMatcher.js` here, and hera-ai's validator) but
 *   NOT from uniqueness: its label and alternates stay reserved. Product
 *   ruling, HA-832. "Cannot be matched" and "name is free" are different
 *   questions.
 *
 *   The one relaxation: collisions that already existed with the same owners
 *   are not reported, so bad data stays repairable. New ones always are.
 */

/**
 * @typedef {Object} OptionInput
 * @property {string|null} [id]
 * @property {string|null|undefined} option
 * @property {Array<string|null|undefined>|null} [alternateImportValues]
 * @property {boolean|null} [isEnabled]
 */

/**
 * @typedef {'label-vs-label'|'label-vs-synonym'|'synonym-vs-synonym'|'self-synonym-matches-own-label'} ConflictKind
 */

/**
 * @typedef {Object} ConflictOccurrence
 * @property {string|null} [optionId]
 * @property {string} rawValue
 * @property {'label'|'synonym'} source
 */

/**
 * @typedef {Object} UniquenessConflict
 * @property {string} normalizedValue
 * @property {ConflictKind} kind
 * @property {ConflictOccurrence[]} occurrences
 */

/**
 * Normalize a candidate value for comparison. null/undefined/whitespace -> ''.
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function normalizeValue(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim().toLowerCase()
}

/**
 * Build a map of normalizedValue -> occurrences.
 *
 * EVERY option is indexed, disabled ones included - a disabled option still
 * holds its label and alternates (HA-832). There is deliberately no
 * `isEnabled` filter; see the uniqueness rule at the top of this file.
 *
 * Synonyms are dropped when `allowAlternateImportValues` is false, because
 * with the flag off they are dormant: nothing reads them and nothing can be
 * matched against them. Labels are indexed either way.
 *
 * @param {ReadonlyArray<OptionInput>} options
 * @param {boolean} allowAlternateImportValues
 * @returns {Map<string, ConflictOccurrence[]>}
 */
function indexOptions(options, allowAlternateImportValues) {
  const index = new Map()
  for (const option of options) {
    const rawLabel = option && option.option != null ? option.option : ''
    const normalizedLabel = normalizeValue(rawLabel)
    if (normalizedLabel !== '') {
      const bucket = index.get(normalizedLabel) || []
      bucket.push({
        optionId: option ? option.id : undefined,
        rawValue: String(rawLabel),
        source: 'label'
      })
      index.set(normalizedLabel, bucket)
    }

    if (!allowAlternateImportValues) continue

    const synonyms = option && Array.isArray(option.alternateImportValues)
      ? option.alternateImportValues
      : []
    for (const synonym of synonyms) {
      const normalized = normalizeValue(synonym)
      if (normalized === '') continue
      const bucket = index.get(normalized) || []
      bucket.push({
        optionId: option ? option.id : undefined,
        rawValue: String(synonym == null ? '' : synonym),
        source: 'synonym'
      })
      index.set(normalized, bucket)
    }
  }
  return index
}

/**
 * How many times each `optionId:source` owner claims a value.
 *
 * A multiset rather than a set, so repeating the same value twice on one
 * option is visible as a change rather than looking identical to carrying it
 * once.
 *
 * @param {ReadonlyArray<ConflictOccurrence>} entries
 * @returns {Map<string, number>}
 */
function countOwners(entries) {
  const counts = new Map()
  for (const entry of entries) {
    const key = `${entry.optionId == null ? '' : entry.optionId}:${entry.source}`
    counts.set(key, (counts.get(key) || 0) + 1)
  }
  return counts
}

/**
 * @param {Map<string, number>} a
 * @param {Map<string, number>} b
 * @returns {boolean}
 */
function ownerCountsEqual(a, b) {
  if (a.size !== b.size) return false
  for (const [key, count] of a) {
    if (b.get(key) !== count) return false
  }
  return true
}

/**
 * @param {ReadonlyArray<ConflictOccurrence>} occurrences
 * @returns {ConflictKind}
 */
function classifyConflict(occurrences) {
  const labels = occurrences.filter((o) => o.source === 'label')
  const synonyms = occurrences.filter((o) => o.source === 'synonym')

  if (labels.length >= 2) return 'label-vs-label'
  if (synonyms.length >= 2 && labels.length === 0) return 'synonym-vs-synonym'
  if (labels.length === 1 && synonyms.length >= 1) {
    const labelOwner = labels[0].optionId
    const selfMatch = synonyms.some((s) => s.optionId && s.optionId === labelOwner)
    return selfMatch ? 'self-synonym-matches-own-label' : 'label-vs-synonym'
  }
  return 'synonym-vs-synonym'
}

/**
 * Find collisions introduced (or preserved) by the transition from
 * `beforeOptions` to `afterOptions`. Grandfathered collisions are excluded.
 *
 * @param {ReadonlyArray<OptionInput>} beforeOptions
 * @param {ReadonlyArray<OptionInput>} afterOptions
 * @param {boolean} allowAlternateImportValues
 * @returns {UniquenessConflict[]}
 */
export function findIntroducedConflicts(beforeOptions, afterOptions, allowAlternateImportValues) {
  const afterIndex = indexOptions(afterOptions, allowAlternateImportValues)
  const beforeIndex = indexOptions(beforeOptions, allowAlternateImportValues)

  /** @type {UniquenessConflict[]} */
  const conflicts = []

  for (const [normalizedValue, occurrences] of afterIndex.entries()) {
    if (occurrences.length < 2) continue

    const afterOwners = countOwners(occurrences)
    const beforeOwners = countOwners(beforeIndex.get(normalizedValue) || [])

    // Grandfathered only if the exact same owners collided before, the same
    // number of times each.
    //
    // COUNTS, not a Set. The owner key is `optionId:source`, so two identical
    // alternates on the SAME option collapse to one key. Comparing sets then
    // reported "same owners as before" and grandfathered a value the user had
    // just duplicated - an option already carrying `B` once, saved with `B`
    // twice, passed validation. Counting per owner closes that: 1 -> 2 is a
    // change, so it is reported.
    if (ownerCountsEqual(afterOwners, beforeOwners)) continue

    conflicts.push({
      normalizedValue,
      kind: classifyConflict(occurrences),
      occurrences: occurrences.map((o) => ({
        optionId: o.optionId,
        rawValue: o.rawValue,
        source: o.source
      }))
    })
  }

  return conflicts
}

/**
 * Enumerate ALL grandfathered collisions present in the current state. Used by
 * the Settings UI for the amber warning badge.
 *
 * @param {ReadonlyArray<OptionInput>} options
 * @param {boolean} allowAlternateImportValues
 * @returns {UniquenessConflict[]}
 */
export function findGrandfatheredConflicts(options, allowAlternateImportValues) {
  // No early return on the flag any more. Labels are indexed whether or not
  // alternates are enabled, so a list with two options sharing a name has a
  // real collision to report even with the flag off. Previously this returned
  // an empty array and the badge stayed hidden on exactly the lists most
  // likely to be carrying duplicates.
  const index = indexOptions(options, allowAlternateImportValues)
  /** @type {UniquenessConflict[]} */
  const conflicts = []
  for (const [normalizedValue, occurrences] of index.entries()) {
    if (occurrences.length < 2) continue
    conflicts.push({
      normalizedValue,
      kind: classifyConflict(occurrences),
      occurrences: occurrences.map((o) => ({
        optionId: o.optionId,
        rawValue: o.rawValue,
        source: o.source
      }))
    })
  }
  return conflicts
}
